import Papa from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import APIUrls from '../core/ApiUrls';
import Toastify from '../core/Toastify';
import { MakeAxiosRequest } from '../core/utils';

const AiCsv = () => {
    const [canView, setCanView] = useState<boolean>(false);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvData, setCsvData] = useState<any[][]>([]);
    const [prompt, setPrompt] = useState<string>('');
    const [resultAnswers, setResultAnswers] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingTime, setLoadingTime] = useState<number | null>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const answersTableRef = useRef<HTMLDivElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setCsvFile(file);
            Papa.parse(file, {
                complete: result => {
                    setCsvData(result.data as any[][]);
                },
                skipEmptyLines: true,
            });
        }
    };

    useEffect(() => {
        // Scroll to answers column when answers are set
        if (resultAnswers.length > 0 && answersTableRef.current && csvData.length > 0) {
            // Find the last header cell (answers column)
            const table = answersTableRef.current.querySelector('table');
            if (table) {
                const ths = table.querySelectorAll('th');
                if (ths.length > 0) {
                    const lastTh = ths[ths.length - 1] as HTMLElement;
                    lastTh.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            }
        }
    }, [resultAnswers, csvData]);

    const handleSendPromptAndFile = async () => {
        setLoading(true);
        setLoadingTime(0);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = setInterval(() => {
            setLoadingTime(prev => (prev !== null ? prev + 1 : 1));
        }, 1000);
        const formData = new FormData();
        if (!csvFile) {
            Toastify.error('Please upload a CSV file first.');
            return;
        }
        if (!prompt.trim()) {
            Toastify.error('Please enter a prompt.');
            return;
        }
        formData.append('file', csvFile);
        formData.append('prompt', prompt);
        try {
            // Increase timeout to 5 minutes (300,000 ms) for AICSV
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('AICSV'), formData, undefined, 300000);
            const answers = res?.data?.data?.answers;
            if (Array.isArray(answers)) {
                setResultAnswers(answers);
            } else {
                Toastify.error('No answers received from backend.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Stop timer when loading becomes false
    useEffect(() => {
        if (!loading && timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    }, [loading]);

    useEffect(() => {
        const checkAccess = async () => {
            try {
                const res = await MakeAxiosRequest('GET', APIUrls.getURL('AICSV'));
                const allowed = res?.data?.data?.allowed;
                setCanView(allowed === true);
            } catch (error) {
                console.log(error);
            }
        };

        checkAccess();
    }, []);

    if (!canView) {
        return <div>PAGE_NOT_FOUND</div>;
    }

    return (
        <>
            <div className="max-w-[90%] mx-auto mt-10 p-6 bg-white rounded-2xl shadow-lg">
                <h2 className="text-3xl font-bold mb-4 text-gray-900 tracking-wide">CSV Upload & Preview</h2>
                <label htmlFor="csv-upload" className="inline-block px-6 py-2 bg-gradient-to-r from-indigo-500 to-blue-400 text-white rounded-lg font-semibold cursor-pointer mb-5 shadow-md hover:from-indigo-600 hover:to-blue-500 transition">
                    Upload CSV
                    <input id="csv-upload" type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
                </label>
                {csvFile && <div className="my-2 mb-5 text-indigo-500 font-medium">Selected file: {csvFile.name}</div>}
                {csvData.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-xl font-semibold text-gray-700 mb-3">CSV Preview</h3>
                        <div ref={answersTableRef} className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
                            <ResultCsvTable csvData={resultAnswers.length > 0 ? mergeAnswersWithCsv(csvData, resultAnswers) : csvData} />
                            <div className="flex items-center gap-4 mt-2 ml-2">
                                <span className="text-xs text-gray-500">Showing all rows.</span>
                                {resultAnswers.length > 0 && (
                                    <button
                                        className="px-4 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-semibold text-xs transition"
                                        onClick={() => downloadCsv(mergeAnswersWithCsv(csvData, resultAnswers))}
                                    >
                                        Download CSV with Answers
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="fixed bottom-6 right-6 z-50">
                <form
                    className="flex items-end bg-white shadow-xl rounded-2xl px-4 py-2 border border-gray-200 gap-2"
                    style={{ minWidth: 380 }}
                    onSubmit={e => {
                        e.preventDefault();
                        handleSendPromptAndFile();
                    }}>
                    <textarea
                        className="flex-1 outline-none bg-transparent px-2 py-1 text-gray-800 placeholder-gray-400 resize-none max-h-40 min-h-[36px] rounded-md"
                        placeholder="Enter your prompt..."
                        value={prompt}
                        onChange={e => {
                            setPrompt(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        rows={1}
                        style={{ overflow: 'hidden' }}
                        disabled={loading}
                    />
                    <button type="submit" className="ml-2 px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full font-semibold transition" disabled={loading}>
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                                Loading
                            </span>
                        ) : 'Send'}
                    </button>
                    {loading && (
                        <span className="ml-3 text-xs text-gray-500 min-w-[60px]">{loadingTime !== null ? `${loadingTime}s` : ''}</span>
                    )}
                    {!loading && loadingTime !== null && loadingTime > 0 && (
                        <span className="ml-3 text-xs text-green-600 min-w-[60px]">AI processed in {loadingTime}s</span>
                    )}
                </form>
            </div>

            {/* Floating download button on the left */}
            {resultAnswers.length > 0 && (
                <button
                    className="fixed bottom-6 left-6 z-50 px-5 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full font-semibold shadow-lg transition flex items-center gap-2"
                    onClick={() => downloadCsv(mergeAnswersWithCsv(csvData, resultAnswers))}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m0 0l-6-6m6 6l6-6" />
                    </svg>
                    Download CSV with Answers
                </button>
            )}
            {/* Result CSV Table with answers merged */}
            {/* No modal for answers preview, answers are merged in main table above */}
        </>
    );

    // Helper component to render CSV string as a table
    function mergeAnswersWithCsv(csv: any[][], answers: string[]): any[][] {
        if (!csv.length) return [];
        // Add 'answers' column to header
        const header = [...csv[0], 'answers'];
        const merged = [header];
        for (let i = 1; i < csv.length; i++) {
            const row = [...csv[i]];
            row.push(answers[i - 1] !== undefined ? answers[i - 1] : '');
            merged.push(row);
        }
        return merged;
    }

    function ResultCsvTable({ csvData }: { csvData: any[][] }) {
        if (!csvData.length) return <div className="text-gray-400">No data to display.</div>;
        // Find the index of the 'answers' column
        const answersIdx = csvData[0].findIndex((h: string) => h.trim().toLowerCase() === 'answers');
        return (
            <div className="overflow-x-auto">
                <table className="min-w-[400px] w-full border-separate border-spacing-0">
                    <thead>
                        <tr className="bg-indigo-500">
                            {csvData[0].map((header, idx) => (
                                <th key={idx} className={`px-3 py-2 font-bold border-b-2 text-left whitespace-nowrap border-r last:border-r-0 border-indigo-200 ` + (idx === answersIdx ? 'bg-yellow-400 text-gray-900' : 'text-white')} style={{ width: idx === answersIdx ? '320px' : `${100 / csvData[0].length}%` }}>
                                    {header}
                                    {idx === answersIdx && <span className="ml-2 text-xs font-semibold text-yellow-900 bg-yellow-200 px-2 py-1 rounded">AI Answers</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {csvData.slice(1, 101).map((row, rowIdx) => (
                            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-indigo-50' : 'bg-white'}>
                                {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className={`px-3 py-2 border-b border-r border-gray-200 text-gray-700 last:border-r-0 align-top ` + (cellIdx === answersIdx ? 'bg-yellow-50 font-medium' : '')} style={{ width: cellIdx === answersIdx ? '320px' : `${100 / csvData[0].length}%`, whiteSpace: cellIdx === answersIdx ? 'pre-line' : 'normal' }}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function downloadCsv(data: any[][]) {
        const csv = Papa.unparse(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ai-answers.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};

export default AiCsv;
